/**
 * 文件处理函数工具库
 */

import fs, { promises as fsPromises } from "fs";
import path from "path";

/**
 * 向文件追加内容
 *
 * @param { string } filePath - 文件路径
 * @param { string } content - 追加内容
 * @returns { Promise<boolean> }
 */
export const appendContent = (filePath, content) => {
  return new Promise((resolve, reject) => {
    try {
      const wrapContent = `${content}\r\n`;

      fs.appendFileSync(filePath, wrapContent);
      resolve(true);
    } catch (err) {
      reject(err);
    }
  });
};

/**
 * 是否存在目录或者文件
 *
 * @param { string } filePath - 文件路径或者目录路径
 * @returns { Promise<boolean> } - 是否存在文件或目錄
 */
export const isExist = (filePath) => {
  return new Promise((resolve, reject) => {
    const isExist = fs.existsSync(filePath);

    resolve(isExist);
  });
};

/**
 * 创建目录，支持多级目录
 *
 * @param { string } fileDirectory - 目录路径
 * @returns { Promise<boolean> } - 是否创建成功
 */
export const createDir = (fileDirectory) => {
  return new Promise(async (resolve, reject) => {
    try {
      const fileDir = path.join(fileDirectory);
      const isExistDir = await isExist(fileDir);

      if (isExistDir) {
        return resolve(true);
      }

      fs.mkdirSync(fileDir, { recursive: true });

      return resolve(true);
    } catch (err) {
      console.log("mkdir err: ", err);
      reject(err);
    }
  });
};

/**
 * 监听文件变化
 *
 * @param { string } filePath - 文件名路径
 * @param { number } interval - 延迟器
 * @param { (size: number) => void } callback - 回调函数
 */
export const watchFile = (filePath, interval, callback) => {
  fs.watchFile(filePath, { interval }, (current) => {
    callback(current.size);
  });
};

/**
 * 取消监听文件变化事件
 *
 * @param { string } filePath - 文件名路径
 */
export const unWatchFile = (filePath) => {
  fs.unwatchFile(filePath);
};

/**
 * 重命名文件名称
 *
 * @param { string } filePath - 文件名路径
 * @param { string } newFilePath - 新文件名路径
 * @returns { Promise<boolean> } - 是否成功
 */
export const renameFile = (filePath, newFilePath) => {
  return new Promise(async (resolve) => {
    const isExistFile = await isExist(filePath);

    if (isExistFile) {
      try {
        fs.renameSync(filePath, newFilePath);
      } catch (err) {
        console.log("rename file err: ", err);
      }
    } else {
      console.log("rename file not exist");
    }

    resolve(true);
  });
};

/**
 * 复制文件到新路径下
 *
 * @param { string } filePath - 文件名路径
 * @param { string } newFilePath - 新文件名路径
 * @returns { Promise<boolean> }
 */
export const copyFile = (filePath, newFilePath) => {
  return new Promise(async (resolve) => {
    const isExistFile = await isExist(filePath);

    if (isExistFile) {
      try {
        fs.copyFileSync(filePath, newFilePath);
        console.log("copy file success");
      } catch (err) {
        console.log("copy file err: ", err);
      }
    } else {
      console.log("copy file not exist");
    }

    resolve(true);
  });
};

/**
 * 清空文件内容
 *
 * @param { string } filePath - 文件名路径
 * @returns { Promise<boolean> }
 */
export const clearFile = (filePath) => {
  return new Promise((resolve) => {
    fs.writeFileSync(filePath, "");

    resolve(true);
  });
};

/**
 * 读取目录列表，获取过滤后的目录下所有文件名
 *
 * @param { string } fileDir - 目录路径
 * @param { string } filterFile - 过滤获取此名称下的文件
 * @returns { Promise<string[]> } - 文件名列表
 */
export const readDir = (fileDir, filterFile) => {
  return new Promise((resolve) => {
    try {
      const fileNameList: string[] = fs
        .readdirSync(fileDir)
        .filter((name) => name.includes(filterFile));

      resolve(fileNameList);
    } catch (err) {
      console.log("read dir error: ", err);
    }
  });
};

/**
 * 读取文件
 * 
 * @param filePath 文件路径
 * @returns {Promise<Buffer>}
 */
export const readFile = (filePath: string) => {
  return fsPromises.readFile(filePath);
}
